import { ContentSection, PreviewLinks, SelectDirections, Directions, Hero, Anchors, Appointment } from '@/components';
import { StyledContent, StyledButton, StyledSection } from '@/components/styles';
import { useRouter } from 'next/router';
import { usePromise } from '@/hooks';
import { useRef, useMemo } from 'react';
import calcCurrentDirection from '@/lib/calcCurrentDirection';
import api from '@/lib/api';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const AppointmentView = (props) => {
  const { t } = useTranslation('global');
  return (
    <StyledButton style={{ width: '100%' }} size="mid" light arrow {...props}>
      {t('bookAppointment')}
    </StyledButton>
  );
};

export default function DirectionServicesView({ banners, directions, short_content, activeSlug, isServicesIndex = false }) {
  const { t } = useTranslation('global');
  const cancelTokenSource = useRef(axios.CancelToken.source());
  const router = useRouter();

  const resolvedActiveSlug = isServicesIndex ? activeSlug : router.query.typeSlug || activeSlug;

  const defaultDirection = directions[0];
  const cur = useMemo(
    () => calcCurrentDirection(resolvedActiveSlug, directions, defaultDirection),
    [resolvedActiveSlug, directions, defaultDirection]
  );

  const requestDirection =
    resolvedActiveSlug && resolvedActiveSlug !== cur?.slug
      ? resolvedActiveSlug
      : cur?.children?.[0]?.slug || cur?.slug || resolvedActiveSlug;

  const directionsPromise = usePromise(
    () => api.directions({ direction: requestDirection, cancelToken: cancelTokenSource.token }),
    [requestDirection],
    {},
    () => cancelTokenSource.current.cancel()
  );

  const activeSubdirection = useMemo(() => {
    if (!resolvedActiveSlug || resolvedActiveSlug === cur?.slug) {
      return null;
    }

    return (
      cur?.children?.find((item) => item.slug === resolvedActiveSlug) ||
      directionsPromise?.result?.current_direction ||
      null
    );
  }, [resolvedActiveSlug, cur, directionsPromise?.result?.current_direction]);

  const handleClick = (slug) => {
    const active = directions.reduce((acc, cur) => {
      return cur.slug === slug
        ? cur
        : cur.children?.find((i) => i.slug === slug)
          ? cur.children?.find((i) => i.slug === slug)
          : acc;
    }, null);

    if (active && (active.service_count > 0 || active.children?.length)) {
      const targetSlug = active.children?.length ? active.children[0].slug : slug;
      router.push({ pathname: `/${targetSlug}`, query: {} }, { pathname: `/${targetSlug}`, query: {} });
    } else if (slug) {
      router.push({ pathname: `/${slug}`, query: {} }, { pathname: `/${slug}`, query: {} });
    }
  };

  const breadcrumbs = useMemo(() => {
    const base = [
      {
        id: 1,
        title: t('main.titlePage'),
        slug: '',
        type: 'index',
      },
      {
        id: 2,
        title: 'OUR SERVICES',
        slug: '',
        type: 'services',
      },
    ];

    if (isServicesIndex) {
      return base;
    }

    const items = [
      ...base,
      {
        id: 3,
        title: cur?.title || '',
        link: `/${cur?.slug}`,
        slug: cur?.slug || '',
        type: 'direction',
      },
    ];

    if (activeSubdirection) {
      items.push({
        id: 4,
        title: activeSubdirection.title || '',
        link: `/${activeSubdirection.slug}`,
        slug: activeSubdirection.slug || '',
        type: 'subdirection',
      });
    }

    return items;
  }, [isServicesIndex, t, cur, activeSubdirection]);

  return (
    <>
      {!!banners?.length && (
        <Hero data={banners} center={true} size="mid" breadcrumbs={breadcrumbs} asH1 />
      )}
      <StyledContent>
        <div style={{ overflow: 'hidden' }}>
          <StyledContent.Container hiddenMobile={isServicesIndex}>
            {!!directions?.length && <Directions directions={directions} active={cur} onClick={handleClick} center />}
          </StyledContent.Container>
          {isServicesIndex && (
            <StyledContent.Container hiddenDesktop visibleMobile>
              {!!directions?.length && <PreviewLinks data={directions} items linksType="directions" />}
            </StyledContent.Container>
          )}
        </div>
        <StyledContent.Container hiddenMobile={isServicesIndex}>
          <StyledContent.Columns>
            <div>
              <StyledContent.Side>
                {!!cur?.children?.length &&
                  directionsPromise?.status !== 'pending' &&
                  directionsPromise?.result?.current_direction && (
                    <StyledContent.Select>
                      <SelectDirections
                        directions={cur.children}
                        active={directionsPromise?.result?.current_direction}
                        onChange={handleClick}
                        disabled={directionsPromise?.status === 'pending'}
                        scrollTop
                      />
                    </StyledContent.Select>
                  )}
                {!!cur?.children?.length &&
                  directionsPromise?.status !== 'pending' &&
                  directionsPromise?.result?.current_direction && (
                    <Anchors
                      list={cur.children}
                      active={directionsPromise?.result?.current_direction?.slug}
                      handleClick={handleClick}
                    />
                  )}
                <div style={{ marginTop: '20px' }} className="button-wrap">
                  <Appointment render={AppointmentView} />
                </div>
              </StyledContent.Side>
            </div>
            <StyledContent.ColumnMain>
              <StyledContent.Loadable isLoading={directionsPromise?.status === 'pending'}>
                {directionsPromise?.status !== 'pending' && !!directionsPromise?.result?.services && (
                  <ContentSection noTitle>
                    <PreviewLinks data={directionsPromise.result.services} items={3} />
                  </ContentSection>
                )}
              </StyledContent.Loadable>
            </StyledContent.ColumnMain>
          </StyledContent.Columns>
        </StyledContent.Container>
        <StyledContent.Container>
          {short_content && (
            <StyledSection pt="none" pb="large">
              <StyledContent.Description>
                <StyledContent.Html dangerouslySetInnerHTML={{ __html: short_content }} dark />
              </StyledContent.Description>
            </StyledSection>
          )}
          <div></div>
        </StyledContent.Container>
      </StyledContent>
    </>
  );
}
